/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import * as CallActionCreators from '@app/actions/CallActionCreators';
import {
	INCOMING_CALL_OVERLAY_HEIGHT,
	INCOMING_CALL_OVERLAY_OFFSET,
	INCOMING_CALL_OVERLAY_STORAGE_KEY,
	INCOMING_CALL_OVERLAY_WIDTH,
} from '@app/components/voice/IncomingCallOverlayConstants';
import {useIncomingCallPortalRoot} from '@app/components/voice/IncomingCallPortal';
import {IncomingCallUI} from '@app/components/voice/IncomingCallUI';
import AppStorage from '@app/lib/AppStorage';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {UserRecord} from '@app/records/UserRecord';
import AuthenticationStore from '@app/stores/AuthenticationStore';
import CallInitiatorStore from '@app/stores/CallInitiatorStore';
import CallStateStore from '@app/stores/CallStateStore';
import ChannelStore from '@app/stores/ChannelStore';
import MockIncomingCallStore from '@app/stores/MockIncomingCallStore';
import SoundStore from '@app/stores/SoundStore';
import UserStore from '@app/stores/UserStore';
import MediaEngineStore from '@app/stores/voice/MediaEngineFacade';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {AnimatePresence} from 'framer-motion';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal} from 'react-dom';

interface PopoutModel {
	channelId: string;
	initiatorUserId: string | null;
	mockChannel?: ChannelRecord;
	mockInitiator?: UserRecord;
}

interface Position {
	x: number;
	y: number;
}

interface WindowSize {
	width: number;
	height: number;
}

function clampNumber(value: number, min: number, max: number): number {
	return Math.min(Math.max(value, min), max);
}

function getWindowSize(): WindowSize {
	return {
		width: window.innerWidth,
		height: window.innerHeight,
	};
}

function getCenterPosition(windowSize: WindowSize): Position {
	return {
		x: Math.max(0, windowSize.width / 2 - INCOMING_CALL_OVERLAY_WIDTH / 2),
		y: Math.max(0, windowSize.height / 2 - INCOMING_CALL_OVERLAY_HEIGHT / 2),
	};
}

function clampPositionToWindow(position: Position, windowSize: WindowSize): Position {
	const maxX = Math.max(0, windowSize.width - INCOMING_CALL_OVERLAY_WIDTH);
	const maxY = Math.max(0, windowSize.height - INCOMING_CALL_OVERLAY_HEIGHT);
	return {
		x: clampNumber(position.x, 0, maxX),
		y: clampNumber(position.y, 0, maxY),
	};
}

function resolveInitiatorUserId(
	channel: ChannelRecord | null,
	ringing: Array<string>,
	currentUserId: string | null,
): string | null {
	if (channel?.type === ChannelTypes.DM && currentUserId) {
		const otherRecipient = channel.recipientIds.find((id) => id !== currentUserId);
		return otherRecipient ?? ringing[0] ?? null;
	}

	if (ringing.length > 0) {
		const nonCurrent = currentUserId ? ringing.find((id) => id !== currentUserId) : undefined;
		return nonCurrent ?? ringing[0] ?? null;
	}

	return null;
}

function setsEqual(a: Set<string>, b: Set<string>) {
	if (a.size !== b.size) return false;
	for (const v of a) if (!b.has(v)) return false;
	return true;
}

export const IncomingCallManager: React.FC = observer(function IncomingCallManager() {
	const calls = CallStateStore.getActiveCalls();
	const mockCall = MockIncomingCallStore.mockCall;
	const portalRoot = useIncomingCallPortalRoot();
	const currentUserId = AuthenticationStore.currentUserId;

	const [activePopouts, setActivePopouts] = useState<Set<string>>(() => new Set());
	const [windowSize, setWindowSize] = useState<WindowSize>(() => getWindowSize());
	const [basePosition, setBasePosition] = useState<Position>(() => {
		const stored = AppStorage.getJSON<Position>(INCOMING_CALL_OVERLAY_STORAGE_KEY);
		if (stored?.x != null && stored?.y != null) return stored;
		return getCenterPosition(getWindowSize());
	});

	const isInCurrentCall = useCallback(
		(channelId: string) => MediaEngineStore.connected && MediaEngineStore.channelId === channelId,
		[],
	);

	const {nextPopoutIds, popoutModels, hasRingingCalls} = useMemo(() => {
		const nextIds = new Set<string>();
		const models: Array<PopoutModel> = [];
		let hasRinging = false;

		for (const call of calls) {
			const isRingingForCurrentUser =
				Boolean(currentUserId && CallStateStore.isUserPendingRinging(call.channelId, currentUserId)) &&
				!isInCurrentCall(call.channelId) &&
				!CallInitiatorStore.hasInitiated(call.channelId);
			if (isRingingForCurrentUser) {
				hasRinging = true;
			}

			const shouldShow = isRingingForCurrentUser;
			if (!shouldShow) continue;

			const channel = ChannelStore.getChannel(call.channelId) ?? null;
			const initiatorUserId = resolveInitiatorUserId(channel, call.ringing, currentUserId);

			nextIds.add(call.channelId);
			models.push({
				channelId: call.channelId,
				initiatorUserId,
			});
		}

		if (mockCall) {
			hasRinging = true;
			nextIds.add(mockCall.channel.id);
			models.push({
				channelId: mockCall.channel.id,
				initiatorUserId: mockCall.initiator.id,
				mockChannel: mockCall.channel,
				mockInitiator: mockCall.initiator,
			});
		}

		return {
			nextPopoutIds: nextIds,
			popoutModels: models,
			hasRingingCalls: hasRinging,
		};
	}, [calls, currentUserId, isInCurrentCall, mockCall]);

	useEffect(() => {
		setActivePopouts((prev) => (setsEqual(prev, nextPopoutIds) ? prev : nextPopoutIds));
	}, [nextPopoutIds]);

	useEffect(() => {
		const handleResize = () => setWindowSize(getWindowSize());
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		const clamped = clampPositionToWindow(basePosition, windowSize);
		if (clamped.x !== basePosition.x || clamped.y !== basePosition.y) {
			setBasePosition(clamped);
		}
	}, [basePosition, windowSize]);

	const wasRingingRef = useRef(false);
	useEffect(() => {
		const wasRinging = wasRingingRef.current;

		if (hasRingingCalls && !wasRinging) {
			SoundStore.startIncomingRing();
		} else if (!hasRingingCalls && wasRinging) {
			SoundStore.stopIncomingRing();
		}

		wasRingingRef.current = hasRingingCalls;
	}, [hasRingingCalls]);

	useEffect(() => () => SoundStore.stopIncomingRing(), []);

	const maxOverlayX = Math.max(0, windowSize.width - INCOMING_CALL_OVERLAY_WIDTH);
	const maxOverlayY = Math.max(0, windowSize.height - INCOMING_CALL_OVERLAY_HEIGHT);

	const clampedBasePosition = useMemo(
		() => clampPositionToWindow(basePosition, windowSize),
		[basePosition, windowSize],
	);

	const handleAccept = useCallback((channelId: string) => {
		if (MockIncomingCallStore.isMockCall(channelId)) {
			MockIncomingCallStore.clearMockCall();
			return;
		}

		CallActionCreators.joinCall(channelId);
	}, []);

	const handleReject = useCallback((channelId: string) => {
		if (MockIncomingCallStore.isMockCall(channelId)) {
			MockIncomingCallStore.clearMockCall();
			return;
		}

		CallActionCreators.rejectCall(channelId);
	}, []);

	const handleIgnore = useCallback((channelId: string) => {
		if (MockIncomingCallStore.isMockCall(channelId)) {
			MockIncomingCallStore.clearMockCall();
			return;
		}

		CallActionCreators.ignoreCall(channelId);
	}, []);

	const handleDragEnd = useCallback(
		(x: number, y: number) => {
			const clamped = clampPositionToWindow({x, y}, windowSize);
			setBasePosition(clamped);
			AppStorage.setJSON(INCOMING_CALL_OVERLAY_STORAGE_KEY, clamped);
		},
		[windowSize],
	);

	const activePopoutIds = useMemo(() => Array.from(activePopouts), [activePopouts]);

	const renderedCalls = useMemo(() => {
		const modelsById = new Map(popoutModels.map((m) => [m.channelId, m]));

		const positions = new Map<string, Position>();
		let offset = 0;

		for (const channelId of activePopoutIds) {
			const x = clampNumber(clampedBasePosition.x + offset, 0, maxOverlayX);
			const y = clampNumber(clampedBasePosition.y + offset, 0, maxOverlayY);
			positions.set(channelId, {x, y});
			offset += INCOMING_CALL_OVERLAY_OFFSET;
		}

		return activePopoutIds.map((channelId) => {
			const model = modelsById.get(channelId);
			if (!model) return null;

			const channel = model.mockChannel ?? ChannelStore.getChannel(channelId) ?? null;
			const storedInitiator =
				!model.mockInitiator && model.initiatorUserId ? (UserStore.getUser(model.initiatorUserId) ?? null) : null;
			const initiator = model.mockInitiator ?? storedInitiator;
			const position = positions.get(channelId) ?? {x: 0, y: 0};

			return (
				<IncomingCallUI
					key={channelId}
					channel={channel}
					initiator={initiator}
					initialX={position.x}
					initialY={position.y}
					maxX={maxOverlayX}
					maxY={maxOverlayY}
					onAccept={() => handleAccept(channelId)}
					onReject={() => handleReject(channelId)}
					onIgnore={() => handleIgnore(channelId)}
					onDragEnd={handleDragEnd}
				/>
			);
		});
	}, [
		activePopoutIds,
		popoutModels,
		handleAccept,
		handleReject,
		handleIgnore,
		handleDragEnd,
		clampedBasePosition.x,
		clampedBasePosition.y,
		maxOverlayX,
		maxOverlayY,
	]);

	if (renderedCalls.length === 0 || !portalRoot) return null;

	return createPortal(<AnimatePresence>{renderedCalls}</AnimatePresence>, portalRoot);
});
